import { cachingService } from "../../helpers/cache";

const initialState = {
  loading: false,
  loadingLibrary:false,
  items: [],
  currentCreation: null,
  reports: [],
  error: null,
  useMockData: false,
  creations: [],
  allCreations: [],
  filteredCreations: [],
  activeTab: 'all',
  creation: null,
  searchResults: [],
  searchLoading: false,
  searchError: null,
  savedCreations: [],
  view_user: null,
  viewuser_loading: false,
  loadingSubscribers: false,
  loadingSubscriptions: false,
  loadingAddresses: false,
  searchOverlayOpen: false,
  searchOverlayQuery: '',
  searchHasRun: false,
  searchBarFloating: false,
  loadingComments: false,
  comments: [],
  searchActiveTab: 'people', // 'creations' or 'people'
  searchPeopleResults: [],
  searchPeopleLoading: false,
  searchPeopleError: null,
  searchPeopleHasRun: false,
  
  // Pagination state
  my_creations: [],
  currentPage: 1,
  hasMoreCreations: true,
  totalCreations: 0,
  loadingMore: false,
  /** When set, feed was fetched with this category from API (no client-side category filter). */
  feedRequestedCategory: null,
};

function parseFeedPayload(payload) {
  if (payload && typeof payload === "object" && "data" in payload) {
    return {
      data: Array.isArray(payload.data) ? payload.data : [],
      requestedCategory: payload.requestedCategory ?? null,
    };
  }
  const data = Array.isArray(payload) ? payload : [];
  return { data, requestedCategory: null };
}

function filterOutReported(creations, reports) {
  const reportedIds = new Set(
    (reports || []).filter((r) => r.status === "accepted").map((r) => r.creation_id)
  );
  return (creations || []).filter((c) => !reportedIds.has(c.creation_id));
}

function applyFeedCategoryFilter(creations, reports, activeTab, feedRequestedCategory) {
  const list = filterOutReported(creations, reports);
  const noCategoryFilter =
    activeTab === "all" ||
    !activeTab ||
    (feedRequestedCategory && activeTab === feedRequestedCategory);
  if (noCategoryFilter) return list;
  const tabLower = String(activeTab).toLowerCase();
  return list.filter((c) => (c.category || "").toLowerCase() === tabLower);
}

const creationReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'FETCH_CREATIONS_REQUEST':
    case 'FETCH_USER_CREATIONS_REQUEST':
    case 'FILTER_CREATIONS_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
        useMockData: false
      };

    case 'FETCH_MORE_USER_CREATIONS_REQUEST':
      return {
        ...state,
        loadingMore: true,
        error: null,
      };

    case 'FETCH_CREATIONS_SUCCESS': {
      const { data, requestedCategory } = parseFeedPayload(action.payload);
      return {
        ...state,
        loading: false,
        loadingMore: false,
        resetCreations: false,
        feedRequestedCategory: requestedCategory,
        allCreations: data,
        creations: data,
        filteredCreations: applyFeedCategoryFilter(
          data,
          state.reports,
          state.activeTab,
          requestedCategory
        ),
        error: null,
        useMockData: false
      };
    }

    case 'FETCH_CREATIONS_PAGINATE_REQUEST':
      return {
        ...state,
        loadingMore: true,
        error: null,
      };

    case 'FETCH_CREATIONS_SUCCESS_PAGINATE': {
      const { data: newData, requestedCategory } = parseFeedPayload(action.payload);
      const existingIds = new Set((state.creations || []).map((c) => c.creation_id));
      const deduped = newData.filter(
        (c) => c && c.creation_id != null && !existingIds.has(c.creation_id)
      );
      const mergedCreations = [...(state.creations || []), ...deduped];
      const mergedAllCreations = [...(state.allCreations || []), ...deduped];
      const category = requestedCategory ?? state.feedRequestedCategory;
      return {
        ...state,
        loadingMore: false,
        feedRequestedCategory: category,
        creations: mergedCreations,
        allCreations: mergedAllCreations,
        filteredCreations: applyFeedCategoryFilter(
          mergedCreations,
          state.reports,
          state.activeTab,
          category
        ),
        error: null,
      };
    }

    case 'FETCH_USER_CREATIONS_SUCCESS': {
      const creationsData = action.payload?.data || action.payload || [];
      const pagination = action.payload?.pagination || {};
      return {
        ...state,
        loading: false,
        resetCreations: false,
        my_creations: creationsData,
        currentPage: pagination.currentPage || 1,
        hasMoreCreations: pagination.hasMore !== false,
        totalCreations: pagination.total || creationsData.length,
        filteredCreations: state.activeTab === 'all'
          ? creationsData
          : creationsData.filter(creation =>
            creation.category?.toLowerCase() === state.activeTab.toLowerCase()
          ),
        error: null,
        useMockData: false
      };
    }
    case 'FILTER_CREATIONS_SUCCESS':
      return {
        ...state,
        loading: false,
        resetCreations: false,
        creations: action.payload || [],
        filteredCreations: state.activeTab === 'all'
          ? action.payload || []
          : (action.payload || []).filter(creation =>
            creation.category?.toLowerCase() === state.activeTab.toLowerCase()
          ),
        error: null,
        useMockData: false
      };

    case 'FETCH_MORE_USER_CREATIONS_SUCCESS': {
      const moreCreationsData = action.payload?.data || action.payload || [];
      const morePagination = action.payload?.pagination || {};
      return {
        ...state,
        loadingMore: false,
        my_creations: [...state.my_creations, ...moreCreationsData],
        currentPage: morePagination.currentPage || state.currentPage + 1,
        hasMoreCreations: morePagination.hasMore !== false,
        totalCreations: morePagination.total || state.totalCreations + moreCreationsData.length,
        filteredCreations: state.activeTab === 'all'
          ? [...state.my_creations, ...moreCreationsData]
          : [...state.my_creations, ...moreCreationsData].filter(creation =>
            creation.category?.toLowerCase() === state.activeTab.toLowerCase()
          ),
        error: null,
      };
    }

    case 'FETCH_MORE_USER_CREATIONS_FAILURE':
      return {
        ...state,
        loadingMore: false,
        error: action.payload,
      };

    case 'RESET_CREATIONS':
      return {
        ...state,
        creations: [],
        allCreations: [],
        filteredCreations: [],
        feedRequestedCategory: null,
        my_creations: [],
        currentPage: 1,
        hasMoreCreations: true,
        totalCreations: 0,
        resetCreations: true
      }
    case 'SET_LOADING_CREATION':
      return {
        ...state,
        loading: action.payload
      }
    case 'FETCH_CREATIONS_FAILURE':
    case 'FETCH_USER_CREATIONS_FAILURE':
    case 'FILTER_CREATIONS_FAILURE':
      console.error('API Error:', action.payload);
      return {
        ...state,
        loading: false,
        loadingMore: false,
        creations: [],
        allCreations: [],
        my_creations: [],
        error: action.payload,
        useMockData: true
      };

    case 'FETCH_CREATION_DETAIL_REQUEST':
      return {
        ...state,
        loading: true,
        error: null
      };

    case 'FETCH_CREATION_DETAIL_SUCCESS':
      return {
        ...state,
        loading: false,
        creation: action.payload || null,
        error: null
      };

    case 'FETCH_CREATION_DETAIL_FAILURE':
      return {
        ...state,
        loading: false,
        currentCreation: null,
        error: action.payload
      };
    case 'UPDATE_CREATION_SUCCESS': {
      const payload = action.payload;
      const creation_id = payload.creation_id || state.creation?.creation_id;
      
      if (!creation_id) {
        // Fallback to old behavior if no creation_id
        return {
          ...state,
          creation: { ...state.creation, ...payload },
          error: null
        };
      }

      // Helper function to update creation in an array
      const updateCreationInArray = (creations) => {
        if (!creations || !Array.isArray(creations)) return creations;
        return creations.map(creation => 
          creation.creation_id === creation_id 
            ? { ...creation, ...payload }
            : creation
        );
      };

      // Update current creation view
      let updatedCreation = state.creation;
      if (state.creation && state.creation.creation_id === creation_id) {
        updatedCreation = { ...state.creation, ...payload };
        // Update cache with confirmed data
        cachingService.update(`/api/creations/detail/${creation_id}`, updatedCreation);
      }

      // Update all state locations with confirmed server data
      return {
        ...state,
        creation: updatedCreation,
        my_creations: updateCreationInArray(state.my_creations),
        creations: updateCreationInArray(state.creations),
        allCreations: updateCreationInArray(state.allCreations),
        filteredCreations: updateCreationInArray(state.filteredCreations),
        searchResults: updateCreationInArray(state.searchResults),
        savedCreations: updateCreationInArray(state.savedCreations),
        // Update view_user.creations if it exists
        view_user: state.view_user && state.view_user.creations
          ? {
              ...state.view_user,
              creations: updateCreationInArray(state.view_user.creations)
            }
          : state.view_user,
        error: null,
        // Clear optimistic update tracking after successful server confirmation
        _optimisticUpdate: state._optimisticUpdate?.[creation_id]
          ? (() => {
              const newOptimisticUpdate = { ...state._optimisticUpdate };
              delete newOptimisticUpdate[creation_id];
              return newOptimisticUpdate;
            })()
          : state._optimisticUpdate
      };
    }

    case 'OPTIMISTIC_UPDATE_CREATION_THUMBNAIL': {
      const { creation_id, thumbnail_url, previous_thumbnail_url } = action.payload;
      
      // Helper function to update creation in an array
      const updateCreationInArray = (creations) => {
        if (!creations || !Array.isArray(creations)) return creations;
        return creations.map(creation => 
          creation.creation_id === creation_id 
            ? { ...creation, thumbnail_url }
            : creation
        );
      };

      // Update current creation view
      let updatedCreation = state.creation;
      if (state.creation && state.creation.creation_id === creation_id) {
        updatedCreation = { ...state.creation, thumbnail_url };
        // Update cache immediately
        cachingService.update(`/api/creations/detail/${creation_id}`, updatedCreation);
      }

      // Update all state locations
      return {
        ...state,
        creation: updatedCreation,
        my_creations: updateCreationInArray(state.my_creations),
        creations: updateCreationInArray(state.creations),
        allCreations: updateCreationInArray(state.allCreations),
        filteredCreations: updateCreationInArray(state.filteredCreations),
        searchResults: updateCreationInArray(state.searchResults),
        savedCreations: updateCreationInArray(state.savedCreations),
        // Update view_user.creations if it exists
        view_user: state.view_user && state.view_user.creations
          ? {
              ...state.view_user,
              creations: updateCreationInArray(state.view_user.creations)
            }
          : state.view_user,
        // Store previous thumbnail for potential rollback
        _optimisticUpdate: {
          ...state._optimisticUpdate,
          [creation_id]: {
            thumbnail_url: previous_thumbnail_url,
            timestamp: Date.now()
          }
        }
      };
    }

    case 'ROLLBACK_CREATION_THUMBNAIL': {
      const { creation_id } = action.payload;
      const previousState = state._optimisticUpdate?.[creation_id];
      
      if (!previousState) return state; // No previous state to rollback to
      
      const { thumbnail_url: previous_thumbnail_url } = previousState;
      
      // Helper function to update creation in an array
      const updateCreationInArray = (creations) => {
        if (!creations || !Array.isArray(creations)) return creations;
        return creations.map(creation => 
          creation.creation_id === creation_id 
            ? { ...creation, thumbnail_url: previous_thumbnail_url }
            : creation
        );
      };

      // Rollback current creation view
      let updatedCreation = state.creation;
      if (state.creation && state.creation.creation_id === creation_id) {
        updatedCreation = { ...state.creation, thumbnail_url: previous_thumbnail_url };
        // Invalidate cache to force refetch
        cachingService.invalidate(`/api/creations/detail/${creation_id}`, true);
      }

      // Remove from optimistic update tracking
      const newOptimisticUpdate = { ...state._optimisticUpdate };
      delete newOptimisticUpdate[creation_id];

      // Rollback all state locations
      return {
        ...state,
        creation: updatedCreation,
        my_creations: updateCreationInArray(state.my_creations),
        creations: updateCreationInArray(state.creations),
        allCreations: updateCreationInArray(state.allCreations),
        filteredCreations: updateCreationInArray(state.filteredCreations),
        searchResults: updateCreationInArray(state.searchResults),
        savedCreations: updateCreationInArray(state.savedCreations),
        view_user: state.view_user && state.view_user.creations
          ? {
              ...state.view_user,
              creations: updateCreationInArray(state.view_user.creations)
            }
          : state.view_user,
        _optimisticUpdate: newOptimisticUpdate
      };
    }

    case 'SET_REPORTS_REQUEST':
      return {
        ...state,
        loading: true
      };

    case 'SET_REPORTS_SUCCESS':
      return {
        ...state,
        loading: false,
        reports: action.payload || []
      };

    case 'UPDATE_REPORTS':
      return {
        ...state,
        reports: [...action.payload]
      };

    case 'SET_CREATIONS':
      return {
        ...state,
        creations: [...action.payload]
      }

    case 'SET_ACTIVE_TAB':
      return {
        ...state,
        activeTab: action.payload,
        filteredCreations: action.payload === 'all'
          ? state.creations
          : state.creations.filter(creation =>
            creation.category?.toLowerCase() === action.payload.toLowerCase()
          )
      };

    case 'ADD_CREATION_SUCCESS':
      return {
        ...state,
        loading: false,
        creationAdded: true,
        creations: [action.payload, ...state.creations],
        error: null,
      };
    case 'SIGNAL_ADD_CREATION_SUCCESS':
      return {
        ...state,
        creationAdded: action.payload,
      }

    case 'SET_VIEW_CREATION':
      return {
        ...state,
        creation: action.payload
      }
    case 'SET_VIEW_USER':
      return {
        ...state,
        view_user: action.payload
      }
    case 'SET_LIKE_SAVE_CREATION': {
      const { creation_id, isLike = false, isSave = false, actioned = 'save' } = action.payload;
      let existingViewCreation = state.creation;
      const existingCreations = state.creations.map(item => {
        if (item.creation_id === creation_id) {
          return {
            ...item,
            is_liked: actioned === 'like' ? (isLike ? [1] : []) : item.is_liked,
            is_saved: actioned === 'save' ? (isSave ? [1] : []) : item.is_saved,
            total_likes: actioned === 'like' ? item.total_likes + (isLike ? 1 : -1) : item.total_likes,
          };
        }
        return item;
      });
      const existingAllCreations = state.allCreations.map(item => {
        if (item.creation_id === creation_id) {
          return {
            ...item,
            is_liked: actioned === 'like' ? (isLike ? [1] : []) : item.is_liked,
            is_saved: actioned === 'save' ? (isSave ? [1] : []) : item.is_saved,
            total_likes: actioned === 'like' ? item.total_likes + (isLike ? 1 : -1) : item.total_likes,
          };
        }
        return item;
      });

      if (state.creation && state.creation.creation_id === creation_id) {
        existingViewCreation = {
          ...state.creation,
          is_liked: actioned === 'like' ? (isLike ? [1] : []) : state.creation.is_liked,
          is_saved: actioned === 'save' ? (isSave ? [1] : []) : state.creation.is_saved,
          total_likes: actioned === 'like' ? state.creation.total_likes + (isLike ? 1 : -1) : state.creation.total_likes,
        }
        cachingService.update(`/api/creations/detail/${creation_id}`, existingViewCreation);
      } else {
        cachingService.invalidate(`/api/creations/detail/${creation_id}`, true);
      }

      return {
        ...state,
        loading: false,
        creation: existingViewCreation,
        creations: existingCreations,
        allCreations: existingAllCreations,
      };
    }
    case 'FETCH_CREATION_COMMENTS_REQUEST':
      return {
        ...state,
        loadingComments: true,
        comments: [],
      };
    case 'FETCH_CREATION_COMMENTS_SUCCESS':
      return {
        ...state,
        loadingComments: false,
        comments: action.payload || [],
      };
    case 'FETCH_CREATION_COMMENTS_FAILURE':
      return {
        ...state,
        loadingComments: false,
      };
    case 'ADD_COMMENT_SUCCESS': {
      const newComment = action.payload;
      const newCommentsList = [newComment, ...(state.comments || [])];
      let existingViewCreation = state.creation;
      const existingCreations = state.creations.map(item => {
        if (item.creation_id === newComment.creation_id) {
          return { ...item, total_comments: (item.total_comments || 0) + 1 };
        }
        return item;
      });
      const existingAllCreations = state.allCreations.map(item => {
        if (item.creation_id === newComment.creation_id) {
          return { ...item, total_comments: (item.total_comments || 0) + 1 };
        }
        return item;
      });

      if (state.creation && state.creation.creation_id === newComment.creation_id) {
        existingViewCreation = {
          ...state.creation,
          total_comments: (state.creation.total_comments || 0) + 1
        }
        cachingService.update(`/api/creations/detail/${newComment.creation_id}`, existingViewCreation);
      } else {
        cachingService.invalidate(`/api/creations/detail/${newComment.creation_id}`, true);
      }

      // Invalidate cache for the specific creation comments

      cachingService.update(`/api/creations/${newComment.creation_id}/comments`, newCommentsList);

      return {
        ...state,
        loading: false,
        comments: newCommentsList,
        creation: existingViewCreation,
        creations: existingCreations,
        allCreations: existingAllCreations,
      };
    }
    case 'SEARCH_CREATIONS_REQUEST':
      return {
        ...state,
        searchLoading: true,
        searchError: null,
        searchResults: [],
        searchHasRun: true,
        searchBarFloating: true,
      };

    case 'SEARCH_CREATIONS_SUCCESS':
      return {
        ...state,
        searchLoading: false,
        searchResults: action.payload || [],
        searchError: null
      };

    case 'SEARCH_CREATIONS_FAILURE':
      return {
        ...state,
        searchLoading: false,
        searchResults: [],
        searchError: action.payload
      };

    case 'FETCH_SAVED_CREATIONS_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'FETCH_SAVED_CREATIONS_SUCCESS':
      return {
        ...state,
        loading: false,
        savedCreations: action.payload,
        fetchSaved: true,
        error: null,
      };
    case 'FETCH_SAVED_CREATIONS_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case 'SET_VIEWUSER_REQUEST':
      return {
        ...state,
        viewuser_loading: true,
        error: null
      };
    case 'SET_VIEWUSER_SUCCESS':
      return {
        ...state,
        // Merge with existing view_user to preserve any already-loaded sections
        view_user: {
          ...(state.view_user || {}), // Handle null case
          ...action.payload,
          // Preserve count numbers - use new value if provided, otherwise keep existing (don't default to 0 if undefined)
          subscribers_number: action.payload?.subscribers_number !== undefined 
            ? action.payload.subscribers_number 
            : (state.view_user?.subscribers_number !== undefined ? state.view_user.subscribers_number : undefined),
          subscriptions_number: action.payload?.subscriptions_number !== undefined 
            ? action.payload.subscriptions_number 
            : (state.view_user?.subscriptions_number !== undefined ? state.view_user.subscriptions_number : undefined),
          // Only update subscribers/subscriptions/addresses if they're provided
          subscribers: action.payload?.subscribers !== undefined ? action.payload.subscribers : state.view_user?.subscribers,
          subscriptions: action.payload?.subscriptions !== undefined ? action.payload.subscriptions : state.view_user?.subscriptions,
          addresses: action.payload?.addresses !== undefined ? action.payload.addresses : state.view_user?.addresses
        },
        viewuser_loading: false
      }
    case 'SET_VIEWUSER_FAILURE':
      return {
        ...state,
        viewuser_loading: false,
        error: action.payload
      }
    case 'FETCH_USER_SUBSCRIBERS_REQUEST':
      return {
        ...state,
        loadingSubscribers: true,
        error: null
      }
    case 'FETCH_USER_SUBSCRIBERS_SUCCESS':
      return {
        ...state,
        loadingSubscribers: false,
        view_user: {
          ...(state.view_user || {}), // Handle null case
          subscribers: action.payload?.subscribers !== undefined ? action.payload.subscribers : (state.view_user?.subscribers || []),
          // Always update count if provided, otherwise preserve existing
          subscribers_number: action.payload?.subscribers_number !== undefined ? action.payload.subscribers_number : (state.view_user?.subscribers_number ?? 0)
        }
      }
    case 'FETCH_USER_SUBSCRIBERS_FAILURE':
      return {
        ...state,
        loadingSubscribers: false,
        error: action.payload
      }
    case 'FETCH_USER_SUBSCRIPTIONS_REQUEST':
      return {
        ...state,
        loadingSubscriptions: true,
        error: null
      }
    case 'FETCH_USER_SUBSCRIPTIONS_SUCCESS':
      return {
        ...state,
        loadingSubscriptions: false,
        view_user: {
          ...(state.view_user || {}), // Handle null case
          subscriptions: action.payload?.subscriptions !== undefined ? action.payload.subscriptions : (state.view_user?.subscriptions || []),
          // Always update count if provided, otherwise preserve existing
          subscriptions_number: action.payload?.subscriptions_number !== undefined ? action.payload.subscriptions_number : (state.view_user?.subscriptions_number ?? 0)
        }
      }
    case 'FETCH_USER_SUBSCRIPTIONS_FAILURE':
      return {
        ...state,
        loadingSubscriptions: false,
        error: action.payload
      }
    case 'FETCH_USER_ADDRESSES_REQUEST':
      return {
        ...state,
        loadingAddresses: true,
        error: null
      }
    case 'FETCH_USER_ADDRESSES_SUCCESS':
      return {
        ...state,
        loadingAddresses: false,
        view_user: {
          ...state.view_user,
          addresses: action.payload?.addresses || state.view_user?.addresses || []
        }
      }
    case 'FETCH_USER_ADDRESSES_FAILURE':
      return {
        ...state,
        loadingAddresses: false,
        error: action.payload
      }
    case 'SUBSCRIBE_SUCCESS_FROM_OTHERS':
      return {
        ...state,
        ...state.view_user && {
          view_user: {
            ...state.view_user,
            [action.payload.list_used]: [
              ...(state.view_user?.[action.payload.list_used] || []).map(user => user.user_id === action.payload.subscribed_to_id ? { ...user, is_subscribed: action.payload.subscribe } : user),
            ]
            //   [action.payload.list_used]: action.payload.subscribe ?
            //     [...state.view_user?.[action.payload.list_used] || [], action.payload.user]
            //     : (state.view_user?.[action.payload.list_used] || []).filter(user => user.user_id !== action.payload.subscriber_id)
            // }
          }
        },
      };
    case 'SUBSCRIBE_SUCCESS':
      return {
        ...state,
        ...state.view_user && {
          view_user: {
            ...state.view_user,
            is_subscribed: action.payload.subscribe,
            subscribers_number: state.view_user?.subscribers_number + (action.payload.subscribe ? 1 : -1),
            subscribers: action.payload.subscribe ?
              [...state.view_user?.subscribers || [], action.payload.user]
              : (state.view_user?.subscribers || []).filter(user => user && user.user_id !== action.payload.subscriber_id)
          }
        },
        ...state.creation && {
          creation: {
            ...state.creation,
            users: {
              ...state.creation?.users,
              is_subscribed: action.payload.subscribe,
              subscribers_number: state.creation?.users?.subscribers_number + (action.payload.subscribe ? 1 : -1),
              subscribers: action.payload.subscribe ?
                [...state.creation?.users?.subscribers || [], action.payload.user]
                : (state.creation?.users?.subscribers || []).filter(user => user && user.user_id !== action.payload.subscriber_id)
            }
          }
        }
      }

    // OPEN_SEARCH_OVERLAY and CLOSE_SEARCH_OVERLAY removed - replaced by unified search page
    case 'SET_SEARCH_OVERLAY_QUERY':
      return {
        ...state,
        searchOverlayQuery: action.payload,
        searchHasRun: false,
      };

    case 'EXIT_SEARCH_MODE':
      return {
        ...state,
        searchModeActive: false,
        searchOverlayOpen: false,
        searchOverlayQuery: '',
        searchResults: [],
        searchHasRun: false,
        searchBarFloating: false,
      };

    case 'RESET_SEARCH_STATE':
      return {
        ...state,
        searchResults: [],
        searchHasRun: false,
        searchLoading: false,
        searchError: null,
        searchOverlayQuery: '',
        searchPeopleResults: [],
        searchPeopleHasRun: false,
        searchPeopleLoading: false,
        searchPeopleError: null,
      };

    case 'SET_SEARCH_ACTIVE_TAB':
      return {
        ...state,
        searchActiveTab: action.payload,
      };

    case 'SEARCH_PEOPLE_REQUEST':
      return {
        ...state,
        searchPeopleLoading: true,
        searchPeopleError: null,
        searchPeopleResults: [],
        searchPeopleHasRun: true,
      };

    case 'SEARCH_PEOPLE_SUCCESS':
      return {
        ...state,
        searchPeopleLoading: false,
        searchPeopleResults: action.payload || [],
        searchPeopleError: null
      };

    case 'SEARCH_PEOPLE_FAILURE':
      return {
        ...state,
        searchPeopleLoading: false,
        searchPeopleResults: [],
        searchPeopleError: action.payload
      };

    case 'UPDATE_REPORTED_CREATION':
      return {
        ...state,
        ...state.creation && {
          creation: {
            ...state.creation,
            user_report: { status: 'under review' },
          }
        },
        ...state.creations && {
          creations: state.creations.map(creation => {
            if (creation.creation_id === action.payload.creation_id) {
              return { ...creation, user_report: { status: 'under review' } };
            }
            return creation;
          })
        },
      };
    case "UPDATE_REPORTED_USER":
      return {
        ...state,
        ...state.view_user && {
          view_user: {
            ...state.view_user,
            user_report: { status: 'under review' },
          }
        }
      };
    case 'DELETE_CREATION':
      return {
        ...state,
        my_creations: (state.my_creations || []).filter(creation => creation.creation_id !== action.payload.creation_id),
        creations: (state.creations || []).filter(creation => creation.creation_id !== action.payload.creation_id),
        allCreations: (state.allCreations || []).filter(creation => creation.creation_id !== action.payload.creation_id),
        filteredCreations: (state.filteredCreations || []).filter(creation => creation.creation_id !== action.payload.creation_id),
        savedCreations: (state.savedCreations || []).filter(creation => creation.creation_id !== action.payload.creation_id),
        searchResults: (state.searchResults || []).filter(creation => creation.creation_id !== action.payload.creation_id),
      }
    case 'DELETE_FROM_LIBRARY':
      return {
        ...state,
        creation: {
          ...state.creation,
          library: (state.creation?.library || []).filter(el => el.user_id !== action.payload.user_id),
          isAddedToLibrary: false,
        }
      }
    case 'ADD_TO_LIBRARY':
      return {
        ...state,
        creation: {
          ...state.creation,
          library: [...state.creation?.library || [], { user_id: action.payload.user_id }],
          isAddedToLibrary: true,
        }
      }


    case 'FETCH_LIBRARY_CREATIONS_REQUEST':
      return {
        ...state,
        loading: true,
        loadingLibrary:true,
        error: null,
      };
    case 'FETCH_LIBRARY_CREATIONS_SUCCESS':
      return {
        ...state,
        loading: false,
        libraryCreations: action.payload,
        loadingLibrary:false,
        error: null,
      };
    case 'FETCH_LIBRARY_CREATIONS_FAILURE':
      return {
        ...state,
        loading: false,
        loadingLibrary:false,
        error: action.payload,
      };
    case 'RELOAD_CREATION':
      return {
        ...state,
        reload_creation: action.payload,
      };
    default:
      return state;
  }
};

export default creationReducer;

